import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { BookOpen, Award, Trophy, Star, Lock, Play, CheckCircle, Clock, Send, Bell, Zap, TrendingUp } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import TrainingWishForm from '../components/TrainingWishForm';

const LEVEL_CONFIG = [
  { level: 1, min: 0, name: "Lehrling", color: "text-gray-400", bg: "bg-gray-500/20" },
  { level: 2, min: 100, name: "Geselle", color: "text-blue-400", bg: "bg-blue-500/20" },
  { level: 3, min: 300, name: "Fachkraft", color: "text-green-400", bg: "bg-green-500/20" },
  { level: 4, min: 600, name: "Meister", color: "text-yellow-400", bg: "bg-yellow-500/20" },
  { level: 5, min: 1000, name: "Experte", color: "text-purple-400", bg: "bg-purple-500/20" },
];

const BADGE_INFO = {
  first_module: { name: "Erster Kurs", desc: "Ersten Kurs abgeschlossen" },
  five_modules: { name: "Fleißig", desc: "5 Kurse abgeschlossen" },
  perfect_score: { name: "Perfektionist", desc: "100% im Abschlusstest" } };

const MitarbeiterDashboard = ({ employee, onLogout }) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [myCourses, setMyCourses] = useState([]);
  const [availableCourses, setAvailableCourses] = useState([]);
  const [badges, setBadges] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [showRequestDialog, setShowRequestDialog] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [requestMessage, setRequestMessage] = useState('');
  const [pointsData, setPointsData] = useState(null);
  const [certificates, setCertificates] = useState([]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const token = localStorage.getItem('mitarbeiterToken');
      
      // Meine Kurse laden
      const myCoursesRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/mitarbeiter/my-courses?token=${token}`);
      const myCoursesData = await myCoursesRes.json();
      if (myCoursesData.success) {
        setMyCourses(myCoursesData.courses || []);
      }

      // Verfügbare Kurse laden
      const availableRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/mitarbeiter/available-courses?token=${token}`);
      const availableData = await availableRes.json();
      if (availableData.success) {
        setAvailableCourses(availableData.courses || []);
      }

      // Badges laden
      const badgesRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/quiz/badges/${employee.id}`);
      const badgesData = await badgesRes.json();
      if (badgesData.success) {
        setBadges(badgesData.badges || []);
      }

      // SmartVolt-Points laden
      const pointsRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/learning/gamification/points?token=${token}`);
      const pointsJson = await pointsRes.json();
      if (pointsJson.success) {
        setPointsData(pointsJson.points);
      }

      // Meine Zertifikate laden
      const certRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/certificates/mitarbeiter/my?token=${token}`);
      const certJson = await certRes.json();
      if (certJson.success) {
        setCertificates(certJson.certificates || []);
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRequestCourse = async () => {
    if (!selectedCourse) return;
    
    try {
      const token = localStorage.getItem('mitarbeiterToken');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/mitarbeiter/request-course`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token,
          courseId: selectedCourse.id,
          message: requestMessage
        })
      });
      const data = await response.json();
      if (data.success) {
        toast({ title: "Erfolg", description: "Kursanfrage wurde an Ihren Chef gesendet" });
        setShowRequestDialog(false);
        setRequestMessage('');
        setSelectedCourse(null);
        loadData();
      } else {
        toast({ title: "Fehler", description: data.detail || "Anfrage konnte nicht gesendet werden", variant: "destructive" });
      }
    } catch (error) {
      toast({ title: "Fehler", description: "Netzwerkfehler", variant: "destructive" });
    }
  };

  const openRequestDialog = (course) => {
    setSelectedCourse(course);
    setShowRequestDialog(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500"></div>
      </div>
    );
  }

  const completedCount = myCourses.filter(c => c.status === 'completed').length;
  const inProgressCount = myCourses.filter(c => c.progress > 0 && c.status !== 'completed').length;

  return (
    <div className="min-h-screen bg-gradient-to-b from-black to-gray-900">
      {/* Header */}
      <header className="bg-black/50 border-b border-yellow-500/30 sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <img src="/assets/smartvolt-logo.png" alt="Smartvolt" className="h-10" />
            <div>
              <h1 className="text-xl font-bold text-yellow-500">Elite-Schmiede</h1>
              <p className="text-sm text-gray-400">{employee?.companyName || 'Ihr Betrieb'}</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right mr-4">
              <p className="text-white font-medium">{employee?.name}</p>
              <p className="text-xs text-gray-400">Monteur</p>
            </div>
            <Button variant="outline" onClick={onLogout} className="border-gray-700 text-gray-300">
              Abmelden
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        {/* Welcome Banner */}
        <Card className="bg-gradient-to-r from-yellow-500/10 to-transparent border-yellow-500/30 mb-8">
          <CardContent className="py-6">
            <p className="text-2xl font-bold text-white">
              Willkommen in der <span className="text-yellow-500">Elite-Schmiede</span>, {employee?.name?.split(' ')[0]}!
            </p>
            <p className="text-gray-400 mt-2">
              Werde zum Experten - {completedCount} von {myCourses.length} Schulungen abgeschlossen
            </p>
          </CardContent>
        </Card>

        {/* Progress Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <Card className="bg-gray-900/50 border-gray-800">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-400">Meine Kurse</p>
                  <p className="text-3xl font-bold text-white">{myCourses.length}</p>
                </div>
                <BookOpen className="h-10 w-10 text-yellow-500/50" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gray-900/50 border-gray-800">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-400">Abgeschlossen</p>
                  <p className="text-3xl font-bold text-green-500">{completedCount}</p>
                </div>
                <CheckCircle className="h-10 w-10 text-green-500/50" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gray-900/50 border-gray-800">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-400">SmartVolt-Points</p>
                  <p className="text-3xl font-bold text-yellow-500">{pointsData?.total_points || 0}</p>
                </div>
                <Zap className="h-10 w-10 text-yellow-500/50" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* SmartVolt-Points & Level Section */}
        <Card className="bg-gray-900/50 border-gray-800 mb-8" data-testid="gamification-section">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Zap className="h-5 w-5 text-yellow-500" />
              SmartVolt-Points
            </CardTitle>
            <CardDescription>Ihre Fortschritte und Auszeichnungen</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Level & Progress */}
              <div>
                {(() => {
                  const currentLevel = LEVEL_CONFIG.find(l => l.level === (pointsData?.level || 1)) || LEVEL_CONFIG[0];
                  const nextLevel = LEVEL_CONFIG.find(l => l.level === currentLevel.level + 1);
                  const totalPts = pointsData?.total_points || 0;
                  const progressToNext = nextLevel 
                    ? Math.min(100, ((totalPts - currentLevel.min) / (nextLevel.min - currentLevel.min)) * 100)
                    : 100;
                  return (
                    <div className="space-y-4">
                      <div className="flex items-center gap-4">
                        <div className={`w-16 h-16 rounded-2xl ${currentLevel.bg} flex items-center justify-center`}>
                          <TrendingUp className={`w-8 h-8 ${currentLevel.color}`} />
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 uppercase tracking-wider">Aktuelles Level</p>
                          <p className={`text-2xl font-bold ${currentLevel.color}`} data-testid="player-level">{currentLevel.name}</p>
                          <p className="text-sm text-gray-400">Level {currentLevel.level} / 5</p>
                        </div>
                      </div>
                      <div>
                        <div className="flex justify-between text-xs mb-1">
                          <span className="text-gray-400">{totalPts} Punkte</span>
                          <span className="text-gray-500">{nextLevel ? `${nextLevel.min} für ${nextLevel.name}` : 'Max erreicht!'}</span>
                        </div>
                        <div className="h-3 bg-gray-800 rounded-full overflow-hidden">
                          <div className="h-full bg-gradient-to-r from-yellow-500 to-green-500 rounded-full transition-all" style={{ width: `${progressToNext}%` }} />
                        </div>
                      </div>
                      <div className="text-xs text-gray-500">
                        {nextLevel ? `Noch ${nextLevel.min - totalPts} Punkte bis zum nächsten Level` : 'Höchstes Level erreicht!'}
                      </div>
                    </div>
                  );
                })()}
              </div>
              {/* Badges */}
              <div>
                <p className="text-sm text-gray-400 mb-3">Auszeichnungen</p>
                <div className="space-y-2">
                  {pointsData?.badges?.length > 0 ? (
                    pointsData.badges.map((badge, idx) => (
                      <div key={idx} className="flex items-center gap-3 bg-black/30 p-3 rounded-lg border border-yellow-500/20">
                        <Award className="w-6 h-6 text-yellow-500" />
                        <div>
                          <p className="text-white text-sm font-medium">{BADGE_INFO[badge]?.name || badge}</p>
                          <p className="text-xs text-gray-500">{BADGE_INFO[badge]?.desc || ''}</p>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-6 text-gray-600">
                      <Award className="w-8 h-8 mx-auto mb-2 opacity-30" />
                      <p className="text-sm">Schließen Sie Kurse ab, um Badges zu erhalten</p>
                    </div>
                  )}
                </div>
                {pointsData?.points_history?.length > 0 && (
                  <div className="mt-4">
                    <p className="text-xs text-gray-500 mb-2">Letzte Aktivitäten</p>
                    {pointsData.points_history.slice(-3).reverse().map((h, idx) => (
                      <div key={idx} className="flex justify-between text-xs py-1 border-b border-gray-800">
                        <span className="text-gray-400">{h.reason}</span>
                        <span className="text-yellow-500 font-mono">+{h.points}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Meine Zertifikate */}
        <Card className="bg-gray-900/50 border-gray-800 mb-8" data-testid="mitarbeiter-certificates-section">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Award className="h-5 w-5 text-yellow-500" />
              Meine Zertifikate ({certificates.length})
            </CardTitle>
            <CardDescription>Deine bestandenen Kurse — PDF jederzeit herunterladbar</CardDescription>
          </CardHeader>
          <CardContent>
            {certificates.length === 0 ? (
              <div className="text-center py-6 text-gray-500 text-sm">
                <Award className="h-10 w-10 mx-auto mb-2 opacity-40" />
                <p>Noch keine Zertifikate. Bestehe einen Abschlusstest, um dein erstes Zertifikat zu erhalten.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {certificates.map((cert) => (
                  <div
                    key={cert.id}
                    className="rounded-lg border border-yellow-500/25 bg-yellow-500/5 p-4 flex items-start gap-3"
                    data-testid={`mitarbeiter-cert-${cert.certificateNumber}`}
                  >
                    <div className="text-3xl">🎓</div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-semibold truncate">{cert.courseTitle}</p>
                      <p className="text-xs text-gray-400">
                        Ausgestellt: {cert.issueDate ? new Date(cert.issueDate).toLocaleDateString('de-DE') : ''}
                        {cert.expiryDate && (
                          <> · Gueltig bis: {new Date(cert.expiryDate).toLocaleDateString('de-DE')}</>
                        )}
                      </p>
                      <p className="text-xs font-mono text-yellow-400 mt-0.5">{cert.certificateNumber}</p>
                      <div className="flex gap-2 mt-2 flex-wrap">
                        <Button
                          size="sm"
                          onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}${cert.downloadUrl}`, '_blank')}
                          className="bg-yellow-500 text-black hover:bg-yellow-400 text-xs h-7"
                          data-testid={`mitarbeiter-cert-download-${cert.certificateNumber}`}
                        >
                          PDF herunterladen
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            const backend = process.env.REACT_APP_BACKEND_URL;
                            const verifyUrl = `${backend}${cert.verifyUrl}`;
                            const text = encodeURIComponent(
                              `Ich habe die "${cert.courseTitle}" bei SmartVolt Academy erfolgreich absolviert! #Elektrotechnik #DGUV #Weiterbildung`
                            );
                            window.open(
                              `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(verifyUrl)}&text=${text}`,
                              '_blank', 'width=650,height=580'
                            );
                          }}
                          className="border-blue-500/40 text-blue-300 hover:bg-blue-500/10 text-xs h-7"
                          data-testid={`mitarbeiter-cert-share-${cert.certificateNumber}`}
                        >
                          LinkedIn
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            const backend = process.env.REACT_APP_BACKEND_URL;
                            const token = localStorage.getItem('mitarbeiterToken');
                            const url = `${backend}/api/certificates/employee/${cert.id}/social-badge.png?token=${encodeURIComponent(token || '')}`;
                            // Direct-download via temporary anchor
                            const a = document.createElement('a');
                            a.href = url;
                            a.download = `SmartVolt_Badge_${cert.certificateNumber}.png`;
                            document.body.appendChild(a);
                            a.click();
                            a.remove();
                          }}
                          className="border-amber-500/40 text-amber-300 hover:bg-amber-500/10 text-xs h-7"
                          data-testid={`mitarbeiter-cert-badge-${cert.certificateNumber}`}
                        >
                          🎉 Social-Card
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* My Courses */}
        <Card className="bg-gray-900/50 border-gray-800 mb-8">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <BookOpen className="h-5 w-5 text-yellow-500" />
              Meine Schulungen
            </CardTitle>
            <CardDescription>Kurse die für Sie freigeschaltet wurden</CardDescription>
          </CardHeader>
          <CardContent>
            {myCourses.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <BookOpen className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>Noch keine Kurse freigeschaltet</p>
                <p className="text-sm mt-2">Fragen Sie Ihren Chef nach Schulungen</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {myCourses.map((course) => (
                  <Card key={course.id} className="bg-black/50 border-gray-800 hover:border-yellow-500/30 transition-all">
                    <CardContent className="pt-4">
                      <div className="flex justify-between items-start mb-2">
                        <h3 className="text-white font-medium">{course.title}</h3>
                        {course.status === 'completed' ? (
                          <Badge className="bg-green-500/20 text-green-500">Abgeschlossen</Badge>
                        ) : course.progress > 0 ? (
                          <Badge className="bg-blue-500/20 text-blue-500">In Bearbeitung</Badge>
                        ) : (
                          <Badge className="bg-gray-500/20 text-gray-400">Nicht gestartet</Badge>
                        )}
                      </div>
                      <p className="text-sm text-gray-400 mb-4 line-clamp-2">{course.description}</p>
                      
                      {/* Progress Bar */}
                      <div className="mb-4">
                        <div className="flex justify-between text-xs text-gray-500 mb-1">
                          <span>Fortschritt</span>
                          <span>{course.progress || 0}%</span>
                        </div>
                        <div className="w-full bg-gray-800 rounded-full h-2">
                          <div 
                            className="bg-yellow-500 h-2 rounded-full transition-all" 
                            style={{ width: `${course.progress || 0}%` }}
                          />
                        </div>
                      </div>

                      <Button 
                        className="w-full bg-yellow-500 text-black hover:bg-yellow-600"
                        onClick={() => navigate(`/kurs/${course.id}`)}
                      >
                        <Play className="h-4 w-4 mr-2" />
                        {course.progress > 0 ? 'Fortsetzen' : 'Starten'}
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Available Courses (Request from Chef) */}
        <Card className="bg-gray-900/50 border-gray-800">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Star className="h-5 w-5 text-yellow-500" />
              Weitere Schulungen entdecken
            </CardTitle>
            <CardDescription>Fragen Sie Ihren Chef nach diesen Weiterbildungen</CardDescription>
          </CardHeader>
          <CardContent>
            {availableCourses.length === 0 ? (
              <div className="text-center py-4 text-gray-500">
                <p>Alle verfügbaren Kurse sind bereits freigeschaltet</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {availableCourses.map((course) => (
                  <Card key={course.id} className="bg-black/50 border-gray-800 relative">
                    <div className="absolute top-2 right-2">
                      <Lock className="h-5 w-5 text-gray-600" />
                    </div>
                    <CardContent className="pt-4">
                      <h3 className="text-white font-medium mb-2 pr-6">{course.title}</h3>
                      <p className="text-sm text-gray-500 mb-4 line-clamp-2">{course.description}</p>
                      
                      {course.requestPending ? (
                        <Button disabled className="w-full" variant="outline">
                          <Clock className="h-4 w-4 mr-2" /> Anfrage ausstehend
                        </Button>
                      ) : (
                        <Button 
                          className="w-full bg-transparent border border-yellow-500 text-yellow-500 hover:bg-yellow-500/10"
                          onClick={() => openRequestDialog(course)}
                        >
                          <Send className="h-4 w-4 mr-2" /> Beim Chef anfragen
                        </Button>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <TrainingWishForm isAuthenticated={true} className="mt-6" />
      </main>

      {/* Request Course Dialog */}
      <Dialog open={showRequestDialog} onOpenChange={setShowRequestDialog}>
        <DialogContent className="bg-gray-900 border-gray-800">
          <DialogHeader>
            <DialogTitle className="text-white">Kurs anfragen</DialogTitle>
            <DialogDescription>
              Sie möchten <span className="text-yellow-500">{selectedCourse?.title}</span> buchen. Ihr Chef erhält eine Benachrichtigung.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm text-gray-400">Nachricht an Ihren Chef (optional)</label>
              <Textarea 
                value={requestMessage}
                onChange={(e) => setRequestMessage(e.target.value)}
                className="bg-black/50 border-gray-700 text-white mt-2"
                placeholder="z.B. Für das kommende Projekt benötige ich diese Qualifikation..."
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRequestDialog(false)}>Abbrechen</Button>
            <Button onClick={handleRequestCourse} className="bg-yellow-500 text-black">
              <Send className="h-4 w-4 mr-2" /> Anfrage senden
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MitarbeiterDashboard;
